import cn from 'clsx'
import React, { useCallback } from 'react'
import { useDrop } from 'react-dnd'
import { useDispatch } from 'hooks/use-redux'
import type { ExplorerPane as TExplorerPane } from 'types/explorer'
import type { NestingLabel, Track } from 'types/track'
import {
  loadPane,
  updatePane,
} from '@disco/state-management/actions/ExplorerActions'
import { TRACK_NESTING_DND } from '@disco/constants/FeatureTypes'
import { AUDIO_FILE } from '@disco/constants/FileTypes'
import { FILE, TRACK } from '@disco/constants/ItemTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getFilesFromDraggedItem,
  getFileTypeByExt,
} from '@disco/utils/file-utils'
import { isDraggedItemPartOfSelection } from '@disco/utils/selection'
import { DropFilesIndicator } from 'components/ui/drop-indicator'
import { isReadOnlyTrack } from '@disco/utils/tracks'
import useTimeout from 'hooks/use-timeout'

const DRAGHOLD_DELAY = 1000

type Props = {
  children: React.ReactNode
  index?: number
  itemPrefix?: string
  nestingLabel?: NestingLabel
  pane?: TExplorerPane
  track?: Track
  onDragHold?: () => void
  onDrop?: (dropResult: any, draggedItem: any) => void
  addNestedTracksToMain?: (...args: any[]) => any
  uploadFilesToMainTrack?: (...args: any[]) => any
  selectedItems?: any[]
  // TODO: remove when all businesses have TRACK_NESTING_DND
  location?: 'manage-nesting'
  disabled?: boolean
}

export default function TrackDropWrapper({
  children,
  disabled,
  index,
  itemPrefix = 'pane',
  nestingLabel,
  pane,
  track,
  onDragHold,
  onDrop,
  addNestedTracksToMain,
  uploadFilesToMainTrack,
  selectedItems = [],
  // TODO: remove when all businesses have TRACK_NESTING_DND
  location,
}: Props) {
  const dispatch = useDispatch()
  const nestingDndEnabled =
    useFeatureEnabled(TRACK_NESTING_DND) || location === 'manage-nesting'

  const [{ isOver, draggedItem }, dropRef] = useDrop<
    any,
    any,
    { isOver: boolean; draggedItem: any }
  >(
    () => ({
      accept: [FILE, TRACK],
      canDrop: item => {
        if (item?.track) {
          if (isReadOnlyTrack(item.track)) return false
          if (track && isReadOnlyTrack(track)) return false
          if (track && track.id === item.track.id) return false
          return getFileTypeByExt(item.track.ext) === AUDIO_FILE
        }
        if (item?.files) return true
        return false
      },
      drop: (item, monitor) => {
        const dropResult = monitor.getDropResult<any>()
        if (dropResult?.alreadyHandled) {
          return
        }

        if (onDrop) {
          onDrop(dropResult, item)
          return { ...(dropResult ?? {}), alreadyHandled: true }
        }

        if (item?.items && uploadFilesToMainTrack) {
          getFilesFromDraggedItem(item).then((files: File[]) => {
            if (files.length > 0) {
              uploadFilesToMainTrack(
                track,
                files,
                nestingLabel,
                dropResult?.index,
              )
            }
          })
        }

        if (
          item?.track &&
          getFileTypeByExt(item.track.ext) === AUDIO_FILE &&
          addNestedTracksToMain
        ) {
          const trackSelected =
            (selectedItems?.length ?? 0) > 0 &&
            isDraggedItemPartOfSelection(selectedItems ?? [], item)

          if (!trackSelected) {
            addNestedTracksToMain(
              track,
              [item.track],
              nestingLabel,
              dropResult?.index,
            )
          } else {
            const tracks = (selectedItems ?? []).filter(
              (i: any) =>
                i.type === TRACK && getFileTypeByExt(i.ext) === AUDIO_FILE,
            )
            if (tracks.length > 0) {
              addNestedTracksToMain(
                track,
                tracks,
                nestingLabel,
                dropResult?.index,
              )
            }
          }
        }

        return { ...(dropResult ?? {}), alreadyHandled: true }
      },
      collect: monitor => ({
        isOver: monitor.canDrop() && monitor.isOver({ shallow: true }),
        draggedItem: monitor.getItem(),
      }),
    }),
    [
      track,
      nestingLabel,
      onDrop,
      uploadFilesToMainTrack,
      addNestedTracksToMain,
      selectedItems,
    ],
  )

  const handleDragHold = useCallback(() => {
    if (pane && typeof index === 'number') {
      dispatch(updatePane({ ...pane, activeItem: `${itemPrefix}/${index}` }))
      dispatch(
        loadPane({
          index: pane.index + 1,
          title: track.name,
          type: 'track',
          url: track.url,
        }),
      )
    }
    if (onDragHold) {
      onDragHold()
    }
  }, [dispatch, index, pane, track, onDragHold, itemPrefix])

  useTimeout(
    handleDragHold,
    isOver && track && nestingDndEnabled ? DRAGHOLD_DELAY : null,
  )

  if (!nestingDndEnabled || disabled) {
    return children
  }

  return (
    <div
      ref={dropRef}
      className={cn({
        HighlightedDropTarget: isOver,
        'HighlightedDropTarget--blink': isOver && track,
      })}
    >
      {children}
      {isOver && draggedItem?.files && <DropFilesIndicator />}
    </div>
  )
}
